import { Temporal } from '@js-temporal/polyfill';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { invoicesQuery, invoiceState, type InvoiceSummary } from '../api/invoices.ts';
import { meQuery } from '../api/me.ts';
import { dayLabel } from '../api/quotes.ts';
import { PageHeader } from '../components/page-header.tsx';
import { formatMinorUnits } from '../money.ts';

// CB-REQ FIN-01 as built at v1.947. There is no "New invoice": a prepay invoice is drafted when its
// quote is accepted, and an account client's arrives with check-in. Drafts come first, because each
// one is waiting on Office to issue it.

export function InvoicesScreen() {
  const invoices = useQuery(invoicesQuery);
  const me = useQuery(meQuery);
  const today = me.data ? Temporal.Now.plainDateISO(me.data.tenant.defaultTz).toString() : '';
  const drafts = invoices.data?.filter((i) => i.status === 'draft').length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Finance"
        title="Invoices"
        meta={
          invoices.data
            ? `${invoices.data.length} ${invoices.data.length === 1 ? 'invoice' : 'invoices'}${
                drafts > 0 ? ` · ${drafts} to issue` : ''
              }`
            : undefined
        }
        actions={
          me.data?.canSeePrices && me.data.capabilities.includes('invoices.view') ? (
            <span className="flex gap-4">
              <Link to="/credit-notes" className="link text-sm">
                Credits
              </Link>
              <Link to="/statements" className="link text-sm">
                Statements
              </Link>
            </span>
          ) : null
        }
      />
      {invoices.isPending ? <p className="text-muted">Loading…</p> : null}
      {invoices.isError ? (
        <p role="alert" className="alert">
          {invoices.error.message}
        </p>
      ) : null}
      {invoices.data?.length === 0 ? (
        <p className="text-ink-soft">
          No invoices yet. A prepay client’s invoice is drafted when their quote is accepted —{' '}
          <Link to="/quotes" className="link">
            open the quotes
          </Link>
          .
        </p>
      ) : null}
      {invoices.data && invoices.data.length > 0 ? (
        <InvoiceTable rows={invoices.data} today={today} />
      ) : null}
    </div>
  );
}

function InvoiceTable({ rows, today }: { rows: InvoiceSummary[]; today: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <caption className="sr-only">Invoices: drafts first, then the newest issued</caption>
        <thead>
          <tr>
            <th scope="col">Invoice</th>
            <th scope="col">Client</th>
            <th scope="col">Job</th>
            <th scope="col">Due</th>
            <th scope="col" className="text-right">
              Total
            </th>
            <th scope="col">State</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((i) => {
            const state = invoiceState(i, today);
            return (
              <tr key={i.id}>
                <th scope="row" className="font-semibold">
                  <Link to="/invoices/$invoiceId" params={{ invoiceId: i.id }} className="link">
                    {i.reference ?? 'Draft'}
                  </Link>
                </th>
                <td>{i.clientName}</td>
                <td>
                  <Link to="/jobs/$jobId" params={{ jobId: i.job.id }} className="link">
                    {i.job.reference}
                  </Link>
                </td>
                <td className="text-sm">{i.dueDate ? dayLabel(i.dueDate) : '—'}</td>
                <td className="text-right tabular-nums">
                  {formatMinorUnits(i.total.amountMinor, i.total.currency)}
                </td>
                <td>
                  {/* Unpaid is the normal state of an issued invoice; only Overdue is a problem. */}
                  <span
                    className={
                      state === 'Overdue'
                        ? 'tag tag-accent-2'
                        : state === 'Unpaid'
                          ? 'tag tag-accent'
                          : 'tag'
                    }
                  >
                    {state}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
